/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  EasAlertAudioVideoContentEnum,
  EasAlertMessageRequestTypeEnum
} from "client-code/easalert-spec";
import type { EasAlert, EasAlertMessage } from "client-code/easalert-spec";

import { Log } from "../logging/Log";
import { EamAlertType, EamContentAvType, PushNotificationType } from "./types";
import type {
  PushNotificationSource,
  TEamPushNotification,
  TPushNotification,
  TPushNotificationMetadata
} from "./types";

/**
 * Stage 2 — parse.
 *
 * Converts a wire type plus a deserialized payload into a typed
 * TPushNotification. Pure statics with no side effects beyond logging.
 *
 * Scope boundary: this class only converts wire format into a typed client
 * object. It never decides what to *do* with a notification — that is
 * PushNotificationRouter's job.
 *
 * The signature deliberately takes the request type and payload separately
 * rather than an IWebSocketMessage, so the parser stays transport-independent
 * and any future envelope unwrapping remains a stage-1 concern.
 */
export class WebSocketNotificationParser {
  private static readonly TAG: string = "WebSocketNotificationParser";

  /** MRPC/2 header carrying the correlation id used for dedupe. */
  private static readonly ORIGIN_REQUEST_ID_KEY: string = "Origin-RequestId";

  /** MRPC/2 header carrying the RPC id. */
  private static readonly RPC_ID_KEY: string = "RpcId";

  /** MRPC/2 header carrying the target device body id. */
  private static readonly BODY_ID_KEY: string = "BodyId";

  /**
   * Service `audioVideoContent` values mapped to the local content type.
   *
   * Keyed by the generated EasAlertAudioVideoContentEnum rather than by
   * string literals, so the mapping is bound to the service contract: if
   * the service team adds a fourth value, this Record becomes incomplete
   * and fails to compile until the new value is handled here.
   */
  private static readonly CONTENT_AV_TYPE_BY_WIRE_VALUE: Record<
    EasAlertAudioVideoContentEnum,
    EamContentAvType
  > = {
    [EasAlertAudioVideoContentEnum.AudioOnly]: EamContentAvType.AUDIO_ONLY,
    [EasAlertAudioVideoContentEnum.AudioVideo]: EamContentAvType.AUDIO_VIDEO,
    [EasAlertAudioVideoContentEnum.VideoOnly]: EamContentAvType.VIDEO_ONLY
  };

  /**
   * Map one wire type and payload to a typed notification.
   *
   * FUTURE SCOPE: add one `case` here plus a matching private builder per
   * newly supported RequestType.
   *
   * @param requestType - The MRPC/2 RequestType header value
   * @param payload - The deserialized JSON body
   * @param requestHeaders - All headers from the inbound frame
   * @param source - The transport that delivered the frame
   * @returns The typed notification, or null when the type is unrecognised
   */
  public static toPushNotification(
    requestType: string,
    payload: Record<string, unknown>,
    requestHeaders: Record<string, string>,
    source: PushNotificationSource
  ): TPushNotification | null {
    const metadata: TPushNotificationMetadata =
      WebSocketNotificationParser.toMetadata(requestHeaders, source);

    // Small today by design — this switch is the documented extension seam
    // and grows a case per supported RequestType.
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (requestType) {
      case PushNotificationType.EMERGENCY_ALERT_DISPLAY:
        return WebSocketNotificationParser.toEamPushNotification(
          payload,
          metadata
        );
      default:
        Log.w(
          WebSocketNotificationParser.TAG,
          `toPushNotification(): Unrecognised requestType=${requestType}`
        );
        return null;
    }
  }

  /**
   * Build the transport-independent metadata common to every notification.
   *
   * Absent headers become "" so consumers never need null checks.
   *
   * @param requestHeaders - All headers from the inbound frame
   * @param source - The transport that delivered the frame
   * @returns The populated metadata
   */
  private static toMetadata(
    requestHeaders: Record<string, string>,
    source: PushNotificationSource
  ): TPushNotificationMetadata {
    return {
      originRequestId: WebSocketNotificationParser.readHeader(
        requestHeaders,
        WebSocketNotificationParser.ORIGIN_REQUEST_ID_KEY
      ),
      rpcId: WebSocketNotificationParser.readHeader(
        requestHeaders,
        WebSocketNotificationParser.RPC_ID_KEY
      ),
      bodyId: WebSocketNotificationParser.readHeader(
        requestHeaders,
        WebSocketNotificationParser.BODY_ID_KEY
      ),
      source: source,
      receivedTimeMs: Date.now()
    };
  }

  /**
   * Build a typed EAS notification from an EasAlertMessage wire body.
   *
   * The body wraps the alert fields under `easAlert` and carries its own
   * `requestType` alongside it — the message's self-description under the
   * server's Hermes messaging system. The server has aligned this value with
   * the MRPC/2 `RequestType` header that WebSocketMrpcv2Enveloper uses for
   * dispatch (both are `emergencyAlertMessage`), but that header — mapped
   * onto `IWebSocketMessage.operationName` — remains the single authoritative
   * dispatch key for this whole path; the body-level value is checked only
   * for drift and never influences routing.
   *
   * Every EasAlert field is optional on the wire, so each is normalised to a
   * non-optional value. A notification with empty text is still returned:
   * a text-less force-tune alert is legitimate and must not be dropped.
   *
   * @param payload - The deserialized EasAlertMessage body
   * @param metadata - The header-derived metadata
   * @returns The typed EAS notification
   */
  private static toEamPushNotification(
    payload: Record<string, unknown>,
    metadata: TPushNotificationMetadata
  ): TEamPushNotification {
    WebSocketNotificationParser.checkBodyRequestType(payload);

    const alertPayload: Record<string, unknown> =
      WebSocketNotificationParser.readAlertFields(payload);

    const alertType: EamAlertType = WebSocketNotificationParser.toAlertType(
      WebSocketNotificationParser.readOptionalString(alertPayload, "alertType")
    );
    const contentAvType: EamContentAvType =
      WebSocketNotificationParser.toContentAvType(
        WebSocketNotificationParser.readOptionalString(
          alertPayload,
          "audioVideoContent"
        )
      );

    const notification: TEamPushNotification = {
      notificationType: PushNotificationType.EMERGENCY_ALERT_DISPLAY,
      text: WebSocketNotificationParser.readOptionalString(
        alertPayload,
        "text"
      ),
      mediaUrl: WebSocketNotificationParser.readOptionalString(
        alertPayload,
        "mediaUrl"
      ),
      contentAvType: contentAvType,
      allowUserExit: WebSocketNotificationParser.readOptionalBoolean(
        alertPayload,
        "allowUserExit",
        false
      ),
      alertType: alertType,
      originRequestId: metadata.originRequestId,
      rpcId: metadata.rpcId,
      bodyId: metadata.bodyId,
      source: metadata.source,
      receivedTimeMs: metadata.receivedTimeMs
    };

    Log.i(
      WebSocketNotificationParser.TAG,
      `toEamPushNotification(): alertType=${notification.alertType}, contentAvType=${notification.contentAvType}, ` +
        `allowUserExit=${notification.allowUserExit}, textLength=${notification.text.length}, ` +
        `hasMediaUrl=${notification.mediaUrl.length > 0}`
    );

    return notification;
  }

  /**
   * Locate the EasAlert fields in the wire body, accepting either shape.
   *
   * The schema nests them under `easAlert` alongside the body's own
   * `requestType` (see `EasAlertMessage`), and that is the shape this reads
   * first. A body that carries the alert fields at its root instead is read
   * directly.
   *
   * Tolerating both is not indulgence, it is the difference between a
   * degraded alert and a dangerous one. The envelope arrived recently, with
   * the `emergencyAlertMessage` rename; a client that insists on it and meets
   * a flat body does not fail loudly — every field is independently defaulted,
   * so it puts an alert on screen with no text, no media, and
   * `allowUserExit: false`, which is silent, blank, and impossible to dismiss.
   * An emergency alert is the last place to be strict about a wrapper.
   *
   * Both branches log, because which one ran is the single most useful fact
   * when an alert arrives looking empty.
   *
   * @param payload - The deserialized EasAlertMessage body
   * @returns The object holding the EasAlert fields; {} when neither shape is
   *   recognisable, so every field falls back to its own default
   */
  private static readAlertFields(
    payload: Record<string, unknown>
  ): Record<string, unknown> {
    const nested: Record<string, unknown> =
      WebSocketNotificationParser.readOptionalObject(payload, "easAlert");
    if (Object.keys(nested).length > 0) {
      return nested;
    }

    if (WebSocketNotificationParser.hasAnyAlertField(payload)) {
      Log.w(
        WebSocketNotificationParser.TAG,
        "readAlertFields(): no `easAlert` envelope — reading the alert fields " +
          "from the body root"
      );
      return payload;
    }

    Log.w(
      WebSocketNotificationParser.TAG,
      "readAlertFields(): body carries neither an `easAlert` envelope nor any " +
        "alert field — the alert will display blank, silent and undismissable"
    );
    return nested;
  }

  /**
   * Whether the body carries any EasAlert field at its root.
   *
   * Keyed to `EasAlert` so a field renamed upstream fails to compile here
   * rather than quietly making every body look unrecognisable.
   *
   * @param payload - The deserialized body
   * @returns True when at least one alert field is present at the root
   */
  private static hasAnyAlertField(payload: Record<string, unknown>): boolean {
    const alertKeys: (keyof EasAlert)[] = [
      "text",
      "mediaUrl",
      "audioVideoContent",
      "allowUserExit",
      "alertType"
    ];
    return alertKeys.some((key): boolean => payload[key] !== undefined);
  }

  /**
   * Log a warning when the body's own `requestType` does not match the
   * schema's single expected value.
   *
   * Non-fatal by design, matching {@link toAlertType}'s tolerance for
   * upstream drift: this field plays no part in dispatch — the MRPC/2
   * `RequestType` header already decided that this is an EAS alert before
   * this method ran — so a mismatch or absence is logged and the alert is
   * still parsed and displayed rather than dropped.
   *
   * @param payload - The deserialized EasAlertMessage body
   */
  private static checkBodyRequestType(payload: Record<string, unknown>): void {
    const raw: unknown = payload.requestType;
    if (raw === EasAlertMessageRequestTypeEnum.EmergencyAlertMessage) {
      return;
    }
    Log.w(
      WebSocketNotificationParser.TAG,
      `checkBodyRequestType(): Unexpected body requestType=${String(raw)}, expected ${EasAlertMessageRequestTypeEnum.EmergencyAlertMessage}`
    );
  }

  /**
   * Map the wire `audioVideoContent` value to EamContentAvType.
   *
   * The recognised values come from the service-provided
   * EasAlertAudioVideoContentEnum via CONTENT_AV_TYPE_BY_WIRE_VALUE.
   * Unrecognised and absent values map to NONE, which the service enum has
   * no equivalent for because every EasAlert field is optional.
   *
   * @param value - The raw wire value
   * @returns The matching content type
   */
  private static toContentAvType(value: string): EamContentAvType {
    const mapped: EamContentAvType | undefined =
      WebSocketNotificationParser.CONTENT_AV_TYPE_BY_WIRE_VALUE[
        value as EasAlertAudioVideoContentEnum
      ];

    if (mapped !== undefined) {
      return mapped;
    }

    return EamContentAvType.NONE;
  }

  /**
   * Map the wire `alertType` value to EamAlertType.
   *
   * Unrecognised and absent values map to UNKNOWN and are logged, never
   * dropped — the alert text must still be shown.
   *
   * @param value - The raw wire value
   * @returns The matching alert type
   */
  private static toAlertType(value: string): EamAlertType {
    switch (value) {
      case EamAlertType.EAN:
        return EamAlertType.EAN;
      case EamAlertType.RWT:
        return EamAlertType.RWT;
      default:
        Log.w(
          WebSocketNotificationParser.TAG,
          `toAlertType(): Unrecognised alertType=${value} — normalising to ${EamAlertType.UNKNOWN}`
        );
        return EamAlertType.UNKNOWN;
    }
  }

  /**
   * Read a header value, defaulting to "" when absent.
   *
   * @param requestHeaders - All headers from the inbound frame
   * @param key - The header name
   * @returns The header value, or "" when absent
   */
  private static readHeader(
    requestHeaders: Record<string, string>,
    key: string
  ): string {
    const raw: unknown = requestHeaders[key];
    if (typeof raw === "string") {
      return raw;
    }
    return "";
  }

  /**
   * Read the `easAlert` object out of an EasAlertMessage wire body.
   *
   * Falls back to {} when absent or of the wrong type — every EasAlert field
   * read from the result is independently defaulted, so a malformed or
   * missing envelope still yields a fully-defaulted notification rather than
   * a throw.
   *
   * `key` is constrained to `keyof EasAlertMessage` for the same reason
   * {@link readOptionalString} constrains its key to `keyof EasAlert`: a
   * field renamed upstream fails to compile here rather than silently
   * yielding {} at runtime.
   *
   * @param payload - The deserialized EasAlertMessage body
   * @param key - The field name, as declared by the EasAlertMessage schema
   * @returns The nested object, or {} when absent or of the wrong type
   */
  private static readOptionalObject(
    payload: Record<string, unknown>,
    key: keyof EasAlertMessage
  ): Record<string, unknown> {
    const raw: unknown = payload[key];
    if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    return {};
  }

  /**
   * Read an optional string field, defaulting to "" when absent or when the
   * wire value is not a string.
   *
   * `key` is constrained to `keyof EasAlert`, so every field this parser
   * reads is checked against the service-provided schema: a field renamed
   * or removed upstream fails to compile here rather than silently
   * yielding "" at runtime.
   *
   * @param payload - The deserialized body
   * @param key - The field name, as declared by the EasAlert schema
   * @returns The field value, or "" when absent or of the wrong type
   */
  private static readOptionalString(
    payload: Record<string, unknown>,
    key: keyof EasAlert
  ): string {
    const raw: unknown = payload[key];
    if (typeof raw === "string") {
      return raw;
    }
    return "";
  }

  /**
   * Read an optional boolean field.
   *
   * A wire value of the wrong type is not coerced — the string "true" yields
   * the fallback, not true.
   *
   * @param payload - The deserialized body
   * @param key - The field name, as declared by the EasAlert schema
   * @param fallback - Value used when the field is absent or of the wrong type
   * @returns The field value, or the fallback
   */
  private static readOptionalBoolean(
    payload: Record<string, unknown>,
    key: keyof EasAlert,
    fallback: boolean
  ): boolean {
    const raw: unknown = payload[key];
    if (typeof raw === "boolean") {
      return raw;
    }
    return fallback;
  }
}
